<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import JsSIP from 'jssip';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const sipUA = ref(null);
const sipRegistered = ref(false);
const currentSession = ref(null);
const callState = ref('idle');
const callDirection = ref(null);
const callerNumber = ref('');
const callerName = ref('');
const callDuration = ref(0);
const isMuted = ref(false);
let callTimer = null;
let sipInboxCache = null;

// ActionCable call context - set when backend notifies about incoming PWA call
const callContext = ref(null);
let callContextTimeout = null;

const sipInboxData = computed(() =>
  (inboxes.value || []).find(i => i.channel_type === INBOX_TYPES.SIP)
);

const hasCall = computed(() =>
  ['ringing', 'active', 'ended'].includes(callState.value)
);

const isRinging = computed(() => callState.value === 'ringing');
const isActive = computed(() => callState.value === 'active');

const directionLabel = computed(() =>
  callDirection.value === 'inbound' ? 'Chamada recebida' : 'Chamada realizada'
);

const statusLabel = computed(() => {
  if (callState.value === 'ringing') return 'Tocando...';
  if (callState.value === 'active') return 'Em chamada';
  if (callState.value === 'ended') return 'Encerrada';
  return '';
});

const formattedDuration = computed(() => {
  const m = Math.floor(callDuration.value / 60)
    .toString()
    .padStart(2, '0');
  const s = (callDuration.value % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
});

const displayCallerName = computed(() => {
  if (callContext.value?.caller_name) return callContext.value.caller_name;
  return callerName.value || callerNumber.value;
});

const displayCallerPhone = computed(() => {
  if (callContext.value?.caller_phone) return callContext.value.caller_phone;
  if (callerName.value && callerName.value !== callerNumber.value)
    return callerNumber.value;
  return '';
});

function ensureAudioElement() {
  let audio = document.getElementById('sip-remote-audio');
  if (!audio) {
    audio = document.createElement('audio');
    audio.id = 'sip-remote-audio';
    audio.autoplay = true;
    document.body.appendChild(audio);
  }
  return audio;
}

// Listen for ActionCable call notifications from backend
useEmitter('sip.call_incoming', data => {
  // eslint-disable-next-line no-console
  console.log('[SIP] ActionCable call notification:', data);
  callContext.value = data;

  // Clear context after 30s if no SIP call arrives
  clearTimeout(callContextTimeout);
  callContextTimeout = setTimeout(() => {
    if (callState.value === 'idle') {
      callContext.value = null;
    }
  }, 30000);
});

let ringtoneInterval = null;

function stopRingtone() {
  clearInterval(ringtoneInterval);
  ringtoneInterval = null;
}

function playRingtone() {
  stopRingtone();
  const playBeep = () => {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 440;
      gain.gain.value = 0.3;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.stop(ctx.currentTime + 0.5);
    } catch {
      /* ignore */
    }
  };
  playBeep();
  ringtoneInterval = setInterval(playBeep, 2000);
}

function resetCallState() {
  currentSession.value = null;
  callState.value = 'idle';
  callDirection.value = null;
  callerNumber.value = '';
  callerName.value = '';
  callDuration.value = 0;
  isMuted.value = false;
  callContext.value = null;
  clearInterval(callTimer);
  clearTimeout(callContextTimeout);
  const audio = document.getElementById('sip-remote-audio');
  if (audio) audio.srcObject = null;
}

async function logCall(reason) {
  const inbox = sipInboxCache;
  if (!inbox || !callerNumber.value) return;

  const duration = callDuration.value;
  const m = Math.floor(duration / 60)
    .toString()
    .padStart(2, '0');
  const s = (duration % 60).toString().padStart(2, '0');
  const dStr = duration > 0 ? `${m}:${s}` : 'Nao atendida';
  const dir = callDirection.value === 'inbound' ? 'Recebida' : 'Realizada';

  try {
    const accountId = store.getters.getCurrentAccountId;
    const user = store.getters.getCurrentUser;
    await fetch(`/api/v1/accounts/${accountId}/sip/call_log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        api_access_token: user?.access_token,
      },
      body: JSON.stringify({
        inbox_id: inbox.id,
        caller_number: callContext.value?.caller_phone || callerNumber.value,
        caller_name: callContext.value?.caller_name || callerName.value,
        direction: callDirection.value,
        duration,
        reason,
        message: `📞 ${dir} de ${callContext.value?.caller_name || callerNumber.value} (${callContext.value?.caller_phone || callerNumber.value}) - Duracao: ${dStr} - ${reason}`,
      }),
    });
  } catch {
    // eslint-disable-next-line no-console
    console.error('[SIP] Failed to log call');
  }
}

function endCall(reason) {
  stopRingtone();
  clearInterval(callTimer);
  callState.value = 'ended';
  logCall(reason);
  setTimeout(() => resetCallState(), 2000);
}

function handleIncomingCall(session) {
  if (currentSession.value) {
    session.terminate({ status_code: 486 });
    return;
  }

  currentSession.value = session;
  callState.value = 'ringing';
  callDirection.value = 'inbound';

  const from = session.remote_identity;
  callerNumber.value = from?.uri?.user || 'Desconhecido';
  callerName.value = from?.display_name || callerNumber.value;

  playRingtone();

  session.on('ended', () => endCall('Chamada encerrada'));
  session.on('failed', e => {
    // eslint-disable-next-line no-console
    console.error('[SIP] CALL FAILED cause:', e.cause);
    // eslint-disable-next-line no-console
    console.error('[SIP] CALL FAILED originator:', e.originator);
    // eslint-disable-next-line no-console
    console.error('[SIP] Remote SDP:', session.request?.body || session._request?.body || 'N/A');
    // eslint-disable-next-line no-alert
    alert('SIP FALHOU: ' + e.cause + '\n\nSDP: ' + (session.request?.body || session._request?.body || 'N/A').substring(0, 500));
    endCall(e.cause === 'Rejected' ? 'Rejeitada' : `Falha: ${e.cause}`);
  });
  // Fix SDP: remove L16 and telephone-event/16000 (not supported by WebRTC)
  session.on('sdp', e => {
    if (e.originator === 'remote') {
      e.sdp = e.sdp
        .replace(/a=rtpmap:118 L16\/16000\r?\n/g, '')
        .replace(/a=rtpmap:101 telephone-event\/16000\r?\n/g, '')
        .replace(/a=fmtp:101 0-15\r?\n/g, '')
        .replace(/ ?118/g, '')
        .replace(/ 101\b/g, '');
      // eslint-disable-next-line no-console
      console.log('[SIP] Fixed SDP (removed L16):', e.sdp);
    }
  });
  session.on('peerconnection', e => {
    e.peerconnection.ontrack = ev => {
      ensureAudioElement().srcObject = ev.streams[0];
    };
  });
  session.on('confirmed', () => {
    if (session.connection) {
      const receivers = session.connection.getReceivers();
      if (receivers.length > 0) {
        const stream = new MediaStream();
        receivers.forEach(r => {
          if (r.track) stream.addTrack(r.track);
        });
        ensureAudioElement().srcObject = stream;
      }
    }
  });
}

async function answerCall() {
  if (!currentSession.value || callState.value !== 'ringing') return;
  stopRingtone();

  // Request microphone FIRST to catch permission errors
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // eslint-disable-next-line no-console
    console.log('[SIP] Microphone access granted');

    // Fix SDP: remove L16 and telephone-event/16000 (not supported by Chrome WebRTC)
    const req = currentSession.value._request;
    if (req && req.body) {
      req.body = req.body
        .replace(/a=rtpmap:118 L16\/16000\r?\n/g, '')
        .replace(/a=rtpmap:101 telephone-event\/16000\r?\n/g, '')
        .replace(/a=fmtp:101 0-15\r?\n/g, '')
        .replace(/(m=audio \d+ RTP\/SAVPF)\s*118\b/g, '$1')
        .replace(/\s+101\b/g, '');
      // eslint-disable-next-line no-console
      console.log('[SIP] Fixed SDP:', req.body);
    }

    currentSession.value.answer({
      mediaConstraints: { audio: true, video: false },
      mediaStream: stream,
      pcConfig: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] },
    });
    callState.value = 'active';
    callDuration.value = 0;
    callTimer = setInterval(() => {
      callDuration.value += 1;
    }, 1000);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[SIP] Answer failed:', err.name, err.message);
    // eslint-disable-next-line no-alert
    alert('Erro ao atender: ' + err.message);
    endCall('Falha: ' + err.message);
  }
}

function rejectCall() {
  if (!currentSession.value) return;
  stopRingtone();
  try {
    currentSession.value.terminate({ status_code: 486 });
  } catch {
    /* ignore */
  }
  resetCallState();
}

function hangup() {
  if (!currentSession.value) return;
  stopRingtone();
  try {
    currentSession.value.terminate();
  } catch {
    /* ignore */
  }
  endCall('Chamada encerrada');
}

function toggleMute() {
  if (!currentSession.value) return;
  isMuted.value = !isMuted.value;
  if (isMuted.value) {
    currentSession.value.mute({ audio: true });
  } else {
    currentSession.value.unmute({ audio: true });
  }
}

function connect(inbox) {
  if (sipUA.value) return;
  if (!inbox?.sip_server || !inbox?.sip_extension || !inbox?.sip_password)
    return;

  sipInboxCache = inbox;

  const wsUrl =
    inbox.sip_wss_url ||
    `wss://${inbox.sip_server}:${inbox.wss_port || 8089}/ws`;

  // eslint-disable-next-line no-console
  console.log('[SIP] Connecting to', wsUrl, 'as', inbox.sip_extension);

  const socket = new JsSIP.WebSocketInterface(wsUrl);
  const ua = new JsSIP.UA({
    sockets: [socket],
    uri: `sip:${inbox.sip_extension}@${inbox.sip_server}`,
    password: inbox.sip_password,
    display_name: inbox.sip_caller_id || inbox.name || 'NT Sales',
    register: true,
    session_timers: false,
  });

  ua.on('connected', () => {
    // eslint-disable-next-line no-console
    console.log('[SIP] WebSocket connected');
  });

  ua.on('registered', () => {
    // eslint-disable-next-line no-console
    console.log('[SIP] Registered as', inbox.sip_extension);
    sipRegistered.value = true;
  });

  ua.on('registrationFailed', e => {
    // eslint-disable-next-line no-console
    console.error('[SIP] Registration failed:', e.cause);
    sipRegistered.value = false;
  });

  ua.on('unregistered', () => {
    sipRegistered.value = false;
  });

  ua.on('newRTCSession', e => {
    if (e.session.direction === 'incoming') {
      handleIncomingCall(e.session);
    }
  });

  ua.on('disconnected', () => {
    // eslint-disable-next-line no-console
    console.log('[SIP] Disconnected');
    sipRegistered.value = false;
  });

  ua.start();
  sipUA.value = ua;
}

// Try to find SIP inbox - retry until store is populated
let sipRetryTimer = null;
let sipRetryCount = 0;

function trySipConnect() {
  const list = store.getters['inboxes/getInboxes'] || [];
  const sipInbox = list.find(i => i.channel_type === INBOX_TYPES.SIP);
  if (sipInbox) {
    // eslint-disable-next-line no-console
    console.log('[SIP] Found SIP inbox:', sipInbox.name);
    if (!sipUA.value) {
      connect(sipInbox);
    }
    return;
  }

  sipRetryCount += 1;
  if (sipRetryCount < 20) {
    sipRetryTimer = setTimeout(trySipConnect, 2000);
  }
}

// Auto-connect on mount
onMounted(() => {
  store.dispatch('inboxes/get').then(() => trySipConnect());
  sipRetryTimer = setTimeout(trySipConnect, 3000);
});

onUnmounted(() => {
  clearTimeout(sipRetryTimer);
  stopRingtone();
  clearTimeout(callContextTimeout);
  if (sipUA.value) {
    try {
      sipUA.value.stop();
    } catch {
      /* ignore */
    }
    sipUA.value = null;
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="sipRegistered && !hasCall"
      class="fixed bottom-4 right-4 z-[9998] flex items-center gap-2 bg-n-surface-2 border border-n-weak rounded-lg px-3 py-1.5 shadow-sm"
    >
      <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
      <span class="text-xs text-n-slate-11">SIP Online</span>
    </div>

    <div
      v-if="hasCall"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-n-surface-1 rounded-2xl shadow-2xl w-80 overflow-hidden border border-n-weak"
      >
        <div class="bg-n-surface-2 px-6 py-4 text-center">
          <p class="text-xs text-n-slate-11 uppercase tracking-wide">
            {{ directionLabel }}
          </p>
        </div>

        <div class="px-6 py-6 flex flex-col items-center gap-3">
          <div
            class="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
            :class="[
              isRinging
                ? 'bg-green-500 animate-pulse'
                : isActive
                  ? 'bg-n-blue-9'
                  : 'bg-n-slate-9',
            ]"
          >
            <i class="i-ri-phone-fill text-3xl" />
          </div>

          <div class="text-center">
            <p class="text-lg font-semibold text-n-slate-12">
              {{ displayCallerName }}
            </p>
            <p v-if="displayCallerPhone" class="text-sm text-n-slate-11">
              {{ displayCallerPhone }}
            </p>
          </div>

          <p
            class="text-sm font-medium"
            :class="[
              isRinging
                ? 'text-green-600'
                : isActive
                  ? 'text-n-blue-9'
                  : 'text-n-slate-11',
            ]"
          >
            {{ statusLabel }}
          </p>

          <p v-if="isActive" class="text-2xl font-mono text-n-slate-12">
            {{ formattedDuration }}
          </p>
        </div>

        <div class="px-6 pb-6 flex items-center justify-center gap-4">
          <template v-if="isRinging">
            <button
              class="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg transition-colors"
              @click="rejectCall"
            >
              <i class="i-ri-phone-off-fill text-2xl" />
            </button>
            <button
              class="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white shadow-lg transition-colors animate-bounce"
              @click="answerCall"
            >
              <i class="i-ri-phone-fill text-2xl" />
            </button>
          </template>

          <template v-if="isActive">
            <button
              class="w-12 h-12 rounded-full flex items-center justify-center transition-colors"
              :class="[
                isMuted
                  ? 'bg-yellow-500 text-white'
                  : 'bg-n-surface-2 text-n-slate-12 hover:bg-n-surface-3',
              ]"
              @click="toggleMute"
            >
              <i
                :class="
                  isMuted
                    ? 'i-ri-mic-off-fill text-xl'
                    : 'i-ri-mic-fill text-xl'
                "
              />
            </button>
            <button
              class="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg transition-colors"
              @click="hangup"
            >
              <i class="i-ri-phone-off-fill text-2xl" />
            </button>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>
